/* eslint-disable @typescript-eslint/ban-ts-comment */
import Button from "../Shared/Button";
import Input from "../Shared/Input";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { register } from "../../api/Auth/register";
import { AxiosError } from "axios";
import { ButtonTypes } from "../../utils/consts";
import CountriesSelectbox from "../Shared/CountriesSelectbox";
import { option } from "../Shared/CountriesSelectbox";
import { useState } from "react";
import { countries } from "../../utils/consts";
import Datepicker from "react-tailwindcss-datepicker";
import TandC from "/src/assets/Terms and Conditions Welcome to All About Accounting.pdf";
const validationSchema = Yup.object({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .required("Password is required")
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/,
      "Must Contain 8 Characters, One Uppercase, One Lowercase, One Special Character, and One Number"
    ),
  confirmPassword: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("password"), ""], "Passwords must match"),
  phone: Yup.string()
    .required("Mobile number is required")
    .max(15, "Phone number must not exceed 15 digits"),
  country: Yup.string().required("Country is required"),
});

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

const RegisterForm = () => {
  const [selected, setSelected] = useState<option | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [value, setValue] = useState<DateRange>({
    startDate: null,
    endDate: null,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = location.search; // This will give you something like '?ref=abc&promo=123'
  const { mutate, error } = useMutation({
    mutationFn: register,
    onSuccess: () => {
      navigate(`/login${searchParams}`);
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });
  const handleValueChange = (newValue: DateRange) => {
    setValue(newValue);
    formik.setFieldValue(
      "birthDate",
      newValue.startDate ? newValue.startDate.toISOString().split("T")[0] : ""
    );
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      country: "",
      jobTitle: "",
      birthDate: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const formattedValues = {
        ...values,
        email: values.email.toLowerCase(),
      };
      mutate(formattedValues);
    },
  });

  // Update formik field value when country is selected
  const handleCountrySelect = (option: option | null) => {
    setSelected(option);
    formik.setFieldValue("country", option ? option?.name : "");
  };

  return (
    <div className="flex flex-col md:mt-16 mt-10 text-burntBlack">
      <div className="mb-8 md:text-start text-center">
        <h1 className="font-semibold sm:mb-2 mb-1 sm:text-[42px] text-28px">
          Create a new account
        </h1>
        <p className="font-medium sm:text-xl">Join our community</p>
      </div>
      <form
        onSubmit={formik.handleSubmit}
        className="flex flex-col gap-4"
        action=""
      >
        <div className="flex md:gap-5 gap-2 w-full">
          <Input
            inputStyles="md:h-16 !h-[52px]"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.firstName && formik.errors.firstName
                ? formik.errors.firstName
                : ""
            }
            className="w-full"
            title="First Name"
            placeholder="John"
            name="firstName"
            type="text"
          />
          <Input
            inputStyles="md:h-16 !h-[52px]"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.lastName && formik.errors.lastName
                ? formik.errors.lastName
                : ""
            }
            className="w-full"
            title="Last Name"
            placeholder="Doe"
            name="lastName"
            type="text"
          />
        </div>
        <Input
          inputStyles="md:h-16 !h-[52px]"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.email && formik.errors.email
              ? formik.errors.email
              : ""
          }
          className="w-full"
          title="Email Address"
          placeholder="someone@example.com"
          name="email"
          type="text"
        />
        <Input
          inputStyles="md:h-16 !h-[52px]"
          value={formik.values.jobTitle}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.jobTitle && formik.errors.jobTitle
              ? formik.errors.jobTitle
              : ""
          }
          className="w-full"
          title="Job Title (Optional)"
          placeholder="Accountant"
          name="jobTitle"
          type="text"
        />
        <CountriesSelectbox
          error={
            formik.touched.country && formik.errors.country
              ? formik.errors.country
              : ""
          }
          title="Country"
          titleStyles="!text-base !mb-2"
          className="!h-16"
          placeholder="Select Country"
          name="country"
          selectOptions={countries || []}
          setSelected={handleCountrySelect}
          selected={selected}
        />
        <Input
          inputStyles="md:h-16 !h-[52px]"
          value={formik.values.phone}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.phone && formik.errors.phone
              ? formik.errors.phone
              : ""
          }
          title="Phone Number"
          placeholder="+20XXXXXXXXX"
          name="phone"
          type="tel"
        />
        <div className={`md:space-y-2 space-y-1.5 relative font-medium`}>
          <label className="text-burntBlack md:text-base text-sm">
            Birthdate (Optional)
          </label>
          <Datepicker
            popoverDirection="down"
            containerClassName="z-[999] relative"
            placeholder="24/08/2001"
            toggleClassName="absolute top-1/2 -translate-y-1/2 right-4"
            inputClassName="md:h-16 !h-[52px] border-2 border-black border-opacity-40 block w-full p-4 focus:outline-none hover:border-primary hover:border-opacity-100 transition-all ease-in focus:border-secondary focus:ring-secondary/50 rounded-lg sm:text-base text-sm"
            asSingle={true}
            showShortcuts={true}
            showFooter={true}
            value={value}
            //@ts-expect-error
            onChange={handleValueChange}
          />
        </div>
        <Input
          inputStyles="md:h-16 !h-[52px]"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.password && formik.errors.password
              ? formik.errors.password
              : ""
          }
          title="Password"
          placeholder="**********"
          name="password"
          type="password"
        />
        <Input
          inputStyles="md:h-16 !h-[52px]"
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.confirmPassword && formik.errors.confirmPassword
              ? formik.errors.confirmPassword
              : ""
          }
          title="Confirm Password"
          placeholder="**********"
          name="confirmPassword"
          type="password"
        />
        <div className="flex items-center gap-2 ">
          <input
            type="checkbox"
            id="agree"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="form-checkbox min-h-4 min-w-4 text-red-500 transition duration-150 ease-in-out mt-0.5"
          />
          <label htmlFor="agree" className="sm:text-sm text-xs">
            By submitting your entry, you agree to the{" "}
            <a
              className="underline text-blue-500"
              href={TandC}
              download={"Terms_and_Conditions_TripleA"}
            >
              Terms & Conditions
            </a>
          </label>
        </div>

        <Button
          type={ButtonTypes.SUBMIT}
          disabled={formik.isSubmitting || !agreedToTerms}
          variant="primary"
          className={`w-full md:mt-4 mt-2 flex justify-center items-center md:h-16 h-[54px] ${
            !agreedToTerms ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          Register
        </Button>
        {error instanceof AxiosError && (
          <span className="text-red-500 font-bold text-lg">
            {typeof error?.response?.data?.error === "string"
              ? error?.response?.data?.error
              : typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : error?.message
              ? error.message
              : error?.response?.data?.error
              ? JSON.stringify(error?.response?.data?.error)
              : "An error occurred"}
          </span>
        )}
      </form>

      <p className="font-semibold sm:text-0.5xl w-full text-darkNavy mt-5">
        Already Have An Account?{" "}
        <Link to="/login" className="text-primary">
          Log In
        </Link>
      </p>
    </div>
  );
};

export default RegisterForm;
